import React, { useMemo } from "react";
import { Button, Empty, Select, Table, Tag, Typography } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import { EditorDevice } from "./types";
import { POPUP_Z_INDEX } from "./constants";

const { Text } = Typography;

interface Props {
  devices: EditorDevice[];
  selectedIds: string[];
  /** The site being edited, so its own devices are not labelled as moving. */
  editingId: string | null;
  onChange: (ids: string[]) => void;
  container: HTMLElement | null;
}

/**
 * Add and remove devices, following ManagePermissionsModal: a table of the
 * current selection with a delete button per row, and a single Select below
 * whose options exclude what is already chosen.
 *
 * Nothing here writes: the selection is staged and only committed on Save.
 */
const TrapsiteDeviceList: React.FC<Props> = ({
  devices,
  selectedIds,
  editingId,
  onChange,
  container,
}) => {
  const byUuid = useMemo(
    () => new Map(devices.map((d) => [d.uuid, d])),
    [devices],
  );

  const rows = useMemo(
    () =>
      selectedIds.map(
        (uuid) =>
          byUuid.get(uuid) ?? {
            uuid,
            displayName: uuid,
            trapSiteId: null,
            trapSiteName: null,
            gps: null,
          },
      ),
    [selectedIds, byUuid],
  );

  /**
   * A device belongs to exactly one site, so picking one that already has a
   * home is a move. Grouping says so before the user commits, and the Save
   * confirm then names what is actually moving.
   */
  const options = useMemo(() => {
    const available = devices.filter((d) => !selectedIds.includes(d.uuid));
    const unassigned = available.filter(
      (d) => !d.trapSiteId || d.trapSiteId === editingId,
    );
    const elsewhere = available.filter(
      (d) => d.trapSiteId && d.trapSiteId !== editingId,
    );

    return [
      {
        label: "Unassigned",
        options: unassigned.map((d) => ({
          value: d.uuid,
          label: d.displayName,
        })),
      },
      {
        label: "Assigned to another location",
        options: elsewhere.map((d) => ({
          value: d.uuid,
          label: `${d.displayName} - ${d.trapSiteName ?? "another location"}`,
        })),
      },
    ].filter((group) => group.options.length > 0);
  }, [devices, selectedIds, editingId]);

  return (
    <div className="flex flex-col gap-2">
      <Text type="secondary">Devices</Text>

      {rows.length > 0 ? (
        <Table
          size="small"
          pagination={false}
          rowKey={(row) => row.uuid}
          dataSource={rows}
          showHeader={false}
          columns={[
            {
              title: "Device",
              dataIndex: "displayName",
              render: (name: string, row: EditorDevice) => (
                <div className="flex items-center gap-2">
                  <Text>{name}</Text>
                  {row.trapSiteId && row.trapSiteId !== editingId && (
                    <Tag color="orange">
                      moving from {row.trapSiteName ?? "another location"}
                    </Tag>
                  )}
                </div>
              ),
            },
            {
              title: "",
              align: "right" as const,
              width: 40,
              render: (_: unknown, row: EditorDevice) => (
                <Button
                  type="text"
                  size="small"
                  aria-label={`Remove ${row.displayName}`}
                  icon={<DeleteOutlined />}
                  onClick={() =>
                    onChange(selectedIds.filter((id) => id !== row.uuid))
                  }
                />
              ),
            },
          ]}
        />
      ) : (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No devices at this location"
        />
      )}

      <Select
        className="w-full"
        placeholder="Add a device"
        disabled={options.length === 0}
        options={options}
        // Reset after each pick, so the control reads as an action rather
        // than as a current value.
        value={null}
        onChange={(uuid: string) => onChange([...selectedIds, uuid])}
        showSearch
        optionFilterProp="label"
        getPopupContainer={() => container || document.body}
        // Without this the list opens behind the panel and reads as a Select
        // that simply does not open.
        styles={{ popup: { root: { zIndex: POPUP_Z_INDEX } } }}
      />
    </div>
  );
};

export default TrapsiteDeviceList;
